/**
 * useCustomerDocuments — Issue #174.
 *
 * Admin-side hook for the customer document review queue.
 *
 * 红线：
 *   - 只使用 generated @jinzhuan/api-client；不手写 fetch。
 *   - 所有 mutation 必须带 expectedVersion。
 *   - 错误以 ApiError 抛出后转换为本地 ApiError 接口；不静默吞掉错误。
 */
import { useCallback, useState } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 客户资料审核端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type CustomerDocumentAdminDto =
  ApiComponents['schemas']['CustomerDocumentAdminDto'];
export type AdminApproveCustomerDocumentDto =
  ApiComponents['schemas']['AdminApproveCustomerDocumentDto'];
export type AdminRejectCustomerDocumentDto =
  ApiComponents['schemas']['AdminRejectCustomerDocumentDto'];
export type AdminPublishCustomerDocumentDto =
  ApiComponents['schemas']['AdminPublishCustomerDocumentDto'];
export type AdminRetractCustomerDocumentDto =
  ApiComponents['schemas']['AdminRetractCustomerDocumentDto'];
export type SignedDownloadGrantDto =
  ApiComponents['schemas']['SignedDownloadGrantDto'];

export type CustomerDocumentReviewState =
  CustomerDocumentAdminDto['reviewState'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  // openapi-fetch middleware throws ApiClientError on non-2xx responses, so
  // the catch path supplies the status via the thrown error instead of via
  // the explicit `httpStatus` argument. Fall back to it when the caller did
  // not pass an explicit status (Issue #226).
  const status = httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (status === 409) {
    return {
      message: '资料已被其他操作修改或状态不一致，请刷新后重试',
      code: 'CONFLICT',
    };
  }
  if (status === 403) {
    return { message: '没有权限执行此操作', code: 'FORBIDDEN' };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

export interface ListFilter {
  reviewState?: CustomerDocumentReviewState;
  docKind?: CustomerDocumentAdminDto['docKind'];
  customerId?: string;
  serviceUserId?: string;
}

export function useCustomerDocuments() {
  const [documents, setDocuments] = useState<CustomerDocumentAdminDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDocuments = useCallback(async (filter?: ListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string> = {};
      if (filter?.reviewState) query.reviewState = filter.reviewState;
      if (filter?.docKind) query.docKind = filter.docKind;
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.serviceUserId) query.serviceUserId = filter.serviceUserId;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/customer-documents',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setDocuments([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const approveDocument = useCallback(
    async (
      id: string,
      dto: AdminApproveCustomerDocumentDto,
    ): Promise<CustomerDocumentAdminDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/customer-documents/{id}/approve',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const rejectDocument = useCallback(
    async (
      id: string,
      dto: AdminRejectCustomerDocumentDto,
    ): Promise<CustomerDocumentAdminDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/customer-documents/{id}/reject',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const publishDocument = useCallback(
    async (
      id: string,
      dto: AdminPublishCustomerDocumentDto,
    ): Promise<CustomerDocumentAdminDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/customer-documents/{id}/publish',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const retractDocument = useCallback(
    async (
      id: string,
      dto: AdminRetractCustomerDocumentDto,
    ): Promise<CustomerDocumentAdminDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/customer-documents/{id}/retract',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const fetchDownloadGrant = useCallback(
    async (id: string): Promise<SignedDownloadGrantDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/customer-documents/{id}/download',
          { params: { path: { id } } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  return {
    documents,
    loading,
    error,
    fetchDocuments,
    approveDocument,
    rejectDocument,
    publishDocument,
    retractDocument,
    fetchDownloadGrant,
  };
}
